import { useState, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * useClientSearch
 *
 * Shared debounced client search hook.
 * Used by both the inline <ClientSearch> component and the
 * ClientLinkModal inside Bookings.
 *
 * Returns:
 *   query        — controlled input value
 *   setQuery     — setter (also resets results when cleared)
 *   results      — array of client objects from the API
 *   searching    — boolean spinner flag
 *   search(q)    — trigger a search (debounced 300 ms)
 *   reset()      — clear query + results
 */
export default function useClientSearch() {
  const { authFetch } = useAuth();
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [searching, setSearching] = useState(false);
  const timeout = useRef(null);

  const search = useCallback((q) => {
    clearTimeout(timeout.current);
    if (!q || q.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    timeout.current = setTimeout(async () => {
      try {
        const res  = await authFetch(`/api/clients/search?q=${encodeURIComponent(q.trim())}`);
        const data = await res.json();
        setResults(data.clients ?? []);
      } catch (_) {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, [authFetch]);

  const reset = useCallback(() => {
    clearTimeout(timeout.current);
    setQuery('');
    setResults([]);
    setSearching(false);
  }, []);

  return { query, setQuery, results, setResults, searching, search, reset };
}
